/**
 * ScoreDial — small radial 0–100 dial used by the QualityScoreStep
 * for both the overall score and per-section subscores.
 *
 * Pure SVG so it renders identically on every browser and in
 * screenshot tests. No motion library — a simple CSS transition
 * on the arc's `stroke-dasharray` handles the animate-in on the
 * first render.
 *
 * Colour tokens read from CSS vars (`--v2-accent`,
 * `--v2-textMuted`) so the theme controls the palette. The
 * "neutral" variant (used for kill_switch / low_confidence
 * dimensions) drops to a muted gray so it visually reads
 * "unavailable" without breaking the layout.
 */

import React from "react";

const SIZE = 96;
const STROKE = 10;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function _tone(score, neutral) {
  if (neutral) return "var(--v2-textMuted, #9ca3af)";
  // Score bands — kept in sync with the backend weak-section threshold.
  // Below 60 = warning tone; 60-79 = accent; 80+ = success.
  if (score >= 80) return "var(--v2-success, #34d399)";
  if (score >= 60) return "var(--v2-accent, #5D50C8)";
  return "var(--v2-warning, #f59e0b)";
}

export default function ScoreDial({
  score,
  label,
  neutral = false,
  size = SIZE,
  testId,
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(score ?? 0)));
  const offset = CIRCUMFERENCE - (clamped / 100) * CIRCUMFERENCE;
  const stroke = _tone(clamped, neutral);
  return (
    <div
      data-testid={testId}
      className="flex flex-col items-center gap-1"
      style={{ width: size }}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="-rotate-90"
          aria-hidden="true"
        >
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="var(--v2-border, #2a2a34)"
            strokeWidth={STROKE}
          />
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={stroke}
            strokeWidth={STROKE}
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 700ms ease-out" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span
            className="text-v2-heading-sm font-semibold tracking-tight text-v2-text"
            data-testid={testId ? `${testId}-value` : undefined}
          >
            {neutral ? "—" : clamped}
          </span>
          {!neutral && (
            <span className="text-v2-caption text-v2-textMuted">/ 100</span>
          )}
        </div>
      </div>
      {label && (
        <span className="text-v2-caption uppercase tracking-[0.14em] text-v2-textMuted text-center">
          {label}
        </span>
      )}
    </div>
  );
}
